/**
 * 技能管理 —— 内置/外部安装/自定义 三种来源
 */
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import { toast } from '../../store/toastStore';
import { DOCUMENT_ACCEPT } from '../../utils/fileTypes';

interface Skill {
  id: number; name: string; description: string; prompt_addition: string;
  category: string; source: string; source_url: string; is_active: boolean;
}


const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  builtin: { label: '内置', color: 'bg-primary/10 text-primary' },
  external: { label: '外部', color: 'bg-purple-100 text-purple-700' },
  custom: { label: '自定义', color: 'bg-emerald-100 text-emerald-700' },
};

const AdminSkills: React.FC = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);

  // 新建/编辑
  const [editing, setEditing] = useState<Skill | null>(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = { name: '', description: '', prompt_addition: '', category: 'content' };
  const [form, setForm] = useState(emptyForm);

  // 安装
  const [showInstall, setShowInstall] = useState(false);
  const [installUrl, setInstallUrl] = useState('');
  const [installJson, setInstallJson] = useState('');
  const [installCategory, setInstallCategory] = useState<'content' | 'image'>('content');
  const [installing, setInstalling] = useState(false);

  // 训练
  const [showTrain, setShowTrain] = useState(false);
  const [trainTab, setTrainTab] = useState<'file' | 'text' | 'project'>('file');
  const [trainFile, setTrainFile] = useState<File | null>(null);
  const [trainText, setTrainText] = useState('');
  const [trainProjectId, setTrainProjectId] = useState('');
  const [trainProjects, setTrainProjects] = useState<{id:number, project_name:string}[]>([]);
  const [trainPreview, setTrainPreview] = useState<any>(null);
  const [training, setTraining] = useState(false);
  const [trainSaving, setTrainSaving] = useState(false);

  // 打开训练弹窗时加载项目列表
  const openTrainModal = async () => {
    setShowTrain(true);
    setTrainPreview(null);
    try {
      const res = await adminApi.getProjects();
      setTrainProjects((res.data?.items || res.data || []).map((p: any) => ({ id: p.id, project_name: p.project_name })));
    } catch { setTrainProjects([]); }
  };

  useEffect(() => { loadSkills(); }, []);

  const handleTrain = async () => {
    let hasInput = false;
    if (trainTab === 'file' && trainFile) hasInput = true;
    if (trainTab === 'text' && trainText.trim()) hasInput = true;
    if (trainTab === 'project' && trainProjectId) hasInput = true;
    if (!hasInput) { toast.error('请先选择/输入参考文档'); return; }
    setTraining(true); setTrainPreview(null);
    try {
      const fd = new FormData();
      if (trainTab === 'file' && trainFile) fd.append('file', trainFile);
      if (trainTab === 'text') fd.append('text_data', trainText);
      if (trainTab === 'project') fd.append('project_id', trainProjectId);
      const res = await adminApi.trainSkill(fd);
      setTrainPreview(res.data);
      toast.success('分析完成，请确认后保存');
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || '';
      toast.error('分析失败: ' + msg);
    } finally { setTraining(false); }
  };

  const handleTrainSave = async () => {
    if (!trainPreview || !trainPreview.prompt_addition) { toast.error('无训练结果'); return; }
    setTrainSaving(true);
    try {
      await adminApi.createSkill({
        name: trainPreview.name || '训练的风格',
        description: trainPreview.description || '',
        prompt_addition: trainPreview.prompt_addition,
        category: trainPreview.category || 'content',
      });
      toast.success('已保存为技能');
      setShowTrain(false); setTrainPreview(null); loadSkills();
    } catch (e: any) { toast.error('保存失败: ' + (e?.message || '')); }
    finally { setTrainSaving(false); }
  };

  const loadSkills = async () => {
    setLoading(true);
    try { setSkills((await adminApi.getSkills()).data || []); }
    catch { toast.error('加载失败'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.prompt_addition.trim()) {
      toast.error('名称和提示词不能为空'); return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminApi.updateSkill(editing.id, form);
        toast.success('已更新');
      } else {
        await adminApi.createSkill(form);
        toast.success('已创建');
      }
      setEditing(null); setForm(emptyForm); loadSkills();
    } catch { toast.error('保存失败'); }
    finally { setSaving(false); }
  };

  const handleInstall = async () => {
    if (!installUrl.trim() && !installJson.trim()) {
      toast.error('请输入 URL 或粘贴 JSON'); return;
    }
    setInstalling(true);
    try {
      await adminApi.installSkill({ url: installUrl, json_content: installJson, category: installCategory });
      toast.success('安装成功');
      setShowInstall(false); setInstallUrl(''); setInstallJson(''); setInstallCategory('content'); loadSkills();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || '安装失败');
    }
    finally { setInstalling(false); }
  };

  const handleToggle = async (s: Skill) => {
    try {
      await adminApi.updateSkill(s.id, { is_active: !s.is_active });
      loadSkills();
    } catch { toast.error('操作失败'); }
  };

  const handleDelete = async (s: Skill) => {
    if (s.source === 'builtin') { toast.error('内置技能 不可删除'); return; }
    if (!window.confirm('确认删除？')) return;
    try { await adminApi.deleteSkill(s.id); toast.success('已删除'); loadSkills(); }
    catch { toast.error('删除失败'); }
  };

  const handleEdit = (s: Skill) => {
    if (s.source === 'builtin') { toast.error('内置技能 只能启用/禁用'); return; }
    setEditing(s);
    setForm({ name: s.name, description: s.description, prompt_addition: s.prompt_addition, category: s.category });
  };

  const builtinSkills = skills.filter(s => s.source === 'builtin');
  const otherSkills = skills.filter(s => s.source !== 'builtin');

  if (loading) return <div className="text-center py-12 text-muted-foreground">加载中...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-6">技能管理</h1>

      {/* 操作按钮 */}
      <div className="flex gap-3 mb-6">
        <button onClick={() => setShowInstall(true)}
          className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700">
          安装外部技能
        </button>
        <button onClick={openTrainModal}
          className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700">
          训练技能
        </button>
        <button onClick={() => { setEditing(null); setForm(emptyForm); }}
          className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/80">
          新建技能
        </button>
      </div>

      {/* 训练技能弹窗 */}
      {showTrain && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center pt-16 z-50" onClick={() => setShowTrain(false)}>
          <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold">训练技能</h3>
              <button onClick={() => setShowTrain(false)} className="text-xl">&times;</button>
            </div>
            <div className="p-6">
              {/* 三个Tab */}
              <div className="flex gap-2 mb-4 bg-accent rounded-lg p-1">
                {(['file','text','project'] as const).map(t => (
                  <button key={t} onClick={() => setTrainTab(t)}
                    className={`flex-1 py-1.5 rounded-md text-sm ${trainTab === t ? 'bg-white shadow-sm font-medium' : ''}`}>
                    {{file:'📁 上传文件',text:'📝 粘贴文本',project:'📂 从项目选'}[t]}
                  </button>
                ))}
              </div>

              {trainTab === 'file' && (
                <input type="file" accept={DOCUMENT_ACCEPT} onChange={e => setTrainFile(e.target.files?.[0] || null)}
                  className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary file:text-white file:text-xs" />
              )}
              {trainTab === 'text' && (
                <textarea value={trainText} onChange={e => setTrainText(e.target.value)}
                  placeholder="粘贴参考方案/投标文件的文本内容（至少200字）" rows={10}
                  className="w-full border rounded-lg p-3 text-sm resize-none" />
              )}
              {trainTab === 'project' && (
                <select value={trainProjectId} onChange={e => setTrainProjectId(e.target.value)}
                  className="w-full border rounded-lg p-2 text-sm">
                  <option value="">选择项目...</option>
                  {trainProjects.map(p => (
                    <option key={p.id} value={String(p.id)}>{p.project_name}</option>
                  ))}
                </select>
              )}

              <button onClick={handleTrain} disabled={training}
                className="mt-4 w-full py-2 bg-primary text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {training ? '分析中...' : '开始分析'}
              </button>

              {/* 训练结果预览 */}
              {trainPreview && (
                <div className="mt-4 p-4 bg-accent rounded-lg space-y-3">
                  <h4 className="text-sm font-semibold">分析结果</h4>
                  <label className="block text-xs text-muted-foreground">名称
                    <input value={trainPreview.name || ''} onChange={e => setTrainPreview({...trainPreview, name: e.target.value})}
                      className="w-full border rounded px-2 py-1 text-sm mt-0.5" />
                  </label>
                  <label className="block text-xs text-muted-foreground">描述
                    <input value={trainPreview.description || ''} onChange={e => setTrainPreview({...trainPreview, description: e.target.value})}
                      className="w-full border rounded px-2 py-1 text-sm mt-0.5" />
                  </label>
                  <label className="block text-xs text-muted-foreground">提示词
                    <textarea value={trainPreview.prompt_addition || ''} onChange={e => setTrainPreview({...trainPreview, prompt_addition: e.target.value})}
                      rows={10} className="w-full border rounded p-2 text-sm mt-0.5 resize-y" />
                  </label>
                  <button onClick={handleTrainSave} disabled={trainSaving}
                    className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                    {trainSaving ? '保存中...' : '确认并保存为技能'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 安装弹窗 */}
      {showInstall && (
        <div className="bg-card rounded-xl border border-purple-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-foreground mb-4">安装外部技能</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">GitHub Raw 地址</label>
              <input value={installUrl} onChange={e => { setInstallUrl(e.target.value); setInstallJson(''); }}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                placeholder="https://raw.githubusercontent.com/.../skill.json" />
            </div>
            <div className="text-center text-xs text-muted-foreground">—— 或直接粘贴 JSON ——</div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">JSON 内容</label>
              <textarea value={installJson} onChange={e => { setInstallJson(e.target.value); setInstallUrl(''); }}
                rows={8} className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono"
                placeholder='{"name":"...","prompt_addition":"...","description":"...","category":"content"}' />
            </div>
            <div className="flex gap-3">
              <button onClick={handleInstall} disabled={installing}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {installing ? '安装中...' : '安装'}
              </button>
              <button onClick={() => setShowInstall(false)}
                className="px-4 py-2 text-sm text-foreground hover:text-foreground">取消</button>
            </div>
          </div>
        </div>
      )}

      {/* 新建/编辑表单 */}
      {editing !== undefined && (editing || !showInstall) && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <h2 className="text-base font-semibold text-foreground mb-4">{editing ? '编辑技能' : '新建技能'}</h2>
          <div className="space-y-4">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm" placeholder="名称: 正式公文风格" />
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm" placeholder="简要描述" />
            <textarea value={form.prompt_addition} onChange={e => setForm(f => ({ ...f, prompt_addition: e.target.value }))}
              rows={6} className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono"
              placeholder="追加到 AI system prompt 的内容..." />
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/80 disabled:opacity-50">
                {saving ? '保存中...' : (editing ? '更新' : '创建')}
              </button>
              <button onClick={() => { setEditing(null); setForm(emptyForm); }}
                className="px-4 py-2 text-sm text-foreground hover:text-foreground">取消</button>
            </div>
          </div>
        </div>
      )}

      {/* 内置技能 */}
      {builtinSkills.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-muted-foreground mb-3">内置技能</h2>
          <SkillTable skills={builtinSkills} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} />
        </div>
      )}

      {/* 已安装技能 */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground mb-3">已安装技能</h2>
        <SkillTable skills={otherSkills} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </div>
  );
};

const SkillTable: React.FC<{
  skills: Skill[];
  onToggle: (s: Skill) => void;
  onEdit: (s: Skill) => void;
  onDelete: (s: Skill) => void;
}> = ({ skills, onToggle, onEdit, onDelete }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-[200px]">名称</th>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-20">来源</th>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-16">状态</th>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-24">操作</th>
        </tr>
      </thead>
      <tbody>
        {skills.map(s => (
          <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="px-4 py-2.5">
              <div className="font-medium text-slate-800 truncate max-w-[180px]" title={s.name}>{s.name}</div>
              {s.description && <div className="text-[11px] text-slate-400 truncate max-w-[180px] mt-0.5" title={s.description}>{s.description}</div>}
            </td>
            <td className="px-4 py-2.5">
              <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${SOURCE_LABELS[s.source]?.color || 'bg-slate-100 text-slate-600'}`}>
                {SOURCE_LABELS[s.source]?.label || s.source}
              </span>
            </td>
            <td className="px-4 py-2.5">
              <button onClick={() => onToggle(s)}
                className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${s.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                {s.is_active ? '启用' : '禁用'}
              </button>
            </td>
            <td className="px-4 py-2.5">
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <button onClick={() => onEdit(s)}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                  {s.source === 'builtin' ? '查看' : '编辑'}
                </button>
                {s.source !== 'builtin' && (
                  <button onClick={() => onDelete(s)} className="text-xs text-rose-500 hover:text-rose-700 font-medium">删除</button>
                )}
              </div>
            </td>
          </tr>
        ))}
        {skills.length === 0 && (
          <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">暂无</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default AdminSkills;
